import React, { useState, useEffect } from "react";
import { TabHeader, OrnateFrame } from "@/components/royal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, VenetianMask } from "lucide-react";

const PREFIXES = [
  "It is whispered that",
  "Some say",
  "It is rumored that",
  "There are whispers that",
  "Word has it that",
  "They say that",
];
const randPrefix = () => PREFIXES[Math.floor(Math.random() * PREFIXES.length)];

export default function ThievesGuild({ settings, saveSettings, editable }) {
  const [rumors, setRumors] = useState(settings.rumors || []);
  const [dirty, setDirty] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { setRumors(settings.rumors || []); setDirty(false); }, [settings.rumors]);

  const mutate = (fn) => { setRumors((prev) => { const n = structuredClone(prev); fn(n); return n; }); setDirty(true); };
  const addRumor = () => {
    const text = draft.trim();
    if (!text) return;
    mutate((r) => r.unshift({ id: crypto.randomUUID(), prefix: randPrefix(), text }));
    setDraft("");
  };
  const setText = (id, v) => mutate((r) => { const x = r.find((y) => y.id === id); if (x) x.text = v; });
  const setPrefix = (id, v) => mutate((r) => { const x = r.find((y) => y.id === id); if (x) x.prefix = v; });
  const rmRumor = (id) => mutate((r) => { const i = r.findIndex((x) => x.id === id); if (i >= 0) r.splice(i, 1); });
  const save = async () => { await saveSettings({ rumors }); setDirty(false); };

  return (
    <div className="space-y-6">
      <TabHeader label="Whispers in the Dark" title="Thieves Guild">
        {editable && dirty && (
          <Button size="sm" className="rounded-sm" onClick={save} data-testid="save-rumors">
            <Save strokeWidth={1.5} className="w-4 h-4 mr-1" /> Save for All
          </Button>
        )}
      </TabHeader>

      {editable && (
        <OrnateFrame className="px-6 pt-7 pb-6 space-y-3" testId="rumor-composer">
          <div className="ht-eyebrow">Spread a New Rumor</div>
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
            placeholder="…the Grey Merchant funds both sides of the war" className="rounded-sm bg-background" data-testid="rumor-input" />
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">A whisper-phrase is added automatically. Sent to every kingdom.</p>
            <Button size="sm" className="rounded-sm" onClick={addRumor} data-testid="add-rumor">
              <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> Add Rumor
            </Button>
          </div>
        </OrnateFrame>
      )}

      {rumors.length === 0 ? (
        <div className="ht-panel p-12 text-center text-muted-foreground" data-testid="rumors-empty">No rumors are circulating… yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rumors-grid">
          {rumors.map((r, i) => (
            <OrnateFrame key={r.id} hover className="px-6 pt-7 pb-6" testId={`rumor-card-${i}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 border border-gold/40 rounded-sm flex items-center justify-center shrink-0">
                  <VenetianMask strokeWidth={1.5} className="w-5 h-5 text-gold" />
                </div>
                <div className="flex-1">
                  <p className="text-base leading-relaxed italic" data-testid={`rumor-text-${i}`}>
                    <span className="text-gold not-italic">{r.prefix} </span>{r.text}
                  </p>
                  {editable && (
                    <div className="mt-3 space-y-2">
                      <Select value={r.prefix} onValueChange={(v) => setPrefix(r.id, v)}>
                        <SelectTrigger className="rounded-sm bg-background h-8 text-xs" data-testid={`rumor-prefix-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{PREFIXES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input value={r.text} onChange={(e) => setText(r.id, e.target.value)} className="rounded-sm bg-background h-8" data-testid={`rumor-edit-${i}`} />
                    </div>
                  )}
                </div>
                {editable && (
                  <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 text-destructive" onClick={() => rmRumor(r.id)} data-testid={`rumor-remove-${i}`}>
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </OrnateFrame>
          ))}
        </div>
      )}
    </div>
  );
}
